import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PartInfo } from "../../../../shared/models";
import {
  setSidewallOnline,
  setSidewallOffline,
  installSidewallPart,
  installExistingSidewallPart,
  uninstallSidewallPart,
  repairSidewallPart,
  upsertSidewallPartDetails,
  createSparePart,
  deleteSparePart,
} from "../../repositories/SidewallRepository";
/** Invalidate all sidewall caches (tree + details). */
const invalidateAll = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ["sidewall-tree"] });
  qc.invalidateQueries({ queryKey: ["sidewall-details"] });
};

// =============================================================================
// Set Online / Set Offline
// =============================================================================

interface SetSidewallOnlineParams {
  sidewallPartId: number;
  parentPartId: number;
  timestamp: Date | null;
}

interface SetSidewallOfflineParams {
  sidewallPartId: number;
  timestamp: Date | null;
}

export const useSetSidewallOnlineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetSidewallOnlineParams) => {
      const resp = await setSidewallOnline(
        params.sidewallPartId,
        params.parentPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set sidewall online");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

export const useSetSidewallOfflineMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: SetSidewallOfflineParams) => {
      const resp = await setSidewallOffline(
        params.sidewallPartId,
        undefined,
        undefined,
        params.timestamp,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to set sidewall offline");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Install Part (new + existing)
// =============================================================================

interface InstallSidewallPartParams {
  sidewallPartId: number;
  positionId: number;
  partTypeId: number;
  partInfo: PartInfo;
  comments: string | null;
  userId?: number;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallSidewallPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallSidewallPartParams) => {
      const resp = await installSidewallPart(
        params.sidewallPartId,
        params.positionId,
        params.partTypeId,
        params.partInfo,
        params.comments,
        params.userId,
        params.operatorNumber,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface InstallExistingPartParams {
  existingPartId: number;
  sidewallPartId: number;
  positionId: number;
  comments: string | null;
  userId?: number;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useInstallExistingPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: InstallExistingPartParams) => {
      const resp = await installExistingSidewallPart(
        params.existingPartId,
        params.sidewallPartId,
        params.positionId,
        params.comments,
        params.userId,
        params.operatorNumber,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to install part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Uninstall / Repair
// =============================================================================

interface UninstallPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useUninstallSidewallPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UninstallPartParams) => {
      const resp = await uninstallSidewallPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to uninstall part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface RepairPartParams {
  partId: number;
  comments: string | null;
  operatorNumber?: number;
  timestamp: Date | null;
}

export const useRepairSidewallPartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: RepairPartParams) => {
      const resp = await repairSidewallPart(
        params.partId,
        params.operatorNumber,
        params.comments,
        params.timestamp,
      );
      if (!resp.ok) throw new Error(resp.message ?? "Failed to reset part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

// =============================================================================
// Edit Part Details
// =============================================================================

interface UpsertPartDetailsParams {
  partId: number;
  partInfo: PartInfo;
  comments: string | null;
}

export const useUpsertSidewallPartDetailsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: UpsertPartDetailsParams) => {
      const resp = await upsertSidewallPartDetails(
        params.partId,
        params.partInfo,
        params.comments,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to update part details");
      return resp;
    },
    onSuccess: () => {
      // Details only — no tree structure change
      queryClient.invalidateQueries({ queryKey: ["sidewall-details"] });
    },
  });
};

// =============================================================================
// Spare Parts (create / delete)
// =============================================================================

interface CreateSparePartParams {
  containerId: number;
  partInfo: PartInfo;
  userId: number | null;
}

export const useCreateSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: CreateSparePartParams) => {
      const resp = await createSparePart(
        params.containerId,
        params.partInfo,
        params.userId,
      );
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to create spare part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};

interface DeleteSparePartParams {
  partId: number;
  userId: number | null;
}

export const useDeleteSparePartMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (params: DeleteSparePartParams) => {
      const resp = await deleteSparePart(params.partId, params.userId);
      if (!resp.ok)
        throw new Error(resp.message ?? "Failed to delete spare part");
      return resp;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
    },
  });
};
